import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Panel,
  Select,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatRate } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import type {
  SplitChangeApplied,
  SplitChangePayload,
  SplitChangeRefused,
  SplitChangeRow,
} from './types.ts';
import { captionLine, stamp, subLine } from './vocabulary.tsx';

/**
 * Split change requests — the operator's half of a conversation the agent
 * portal has only ever had one side of.
 *
 * ── WHAT THIS SCREEN IS FOR ────────────────────────────────────────────────
 *
 * A recruiting agent can ask, from their own portal, for a sub-agent's cut to
 * move. That writes one row to `agent.agent_split_change` and stops there,
 * because `dayon_partner` holds no UPDATE on `agent.agent` at all — a partner
 * must not be able to move their own downline's rate. Until now nothing read
 * the row back, so the request was recorded and never acted on, and the portal
 * said so in as many words. This is where somebody acts on it.
 *
 * ── THE FOUR RATES, AND WHY ALL FOUR ARE ON SCREEN ─────────────────────────
 *
 * A request carries the rates as they were when it was raised. The decision has
 * to be taken against the rates as they are now. Those two disagree exactly in
 * the case the cap exists for — a master whose own rate was cut after asking
 * for a sub-agent to go up — and an operator pressing Apply on a stale row
 * would be handing a sub-agent more than its master earns. So the table prints
 * the recorded pair and the live pair side by side, and the server's own
 * `within_cap_now` decides which rows are marked, rather than this component
 * re-deriving the comparison from figures it fetched at a different moment
 * from the ones the route re-reads under its lock.
 *
 * ── THE CAPABILITY IS TEXT, NOT A TOOLTIP ──────────────────────────────────
 *
 * An operator holding `partners.view` and not `partners.manage` reads this
 * queue and cannot decide it. That is said in a sentence in the panel, in the
 * flow of the page. It is deliberately NOT a `title` attribute on a disabled
 * button: browsers suppress the tooltip on a disabled control, so the one
 * reader who needs the explanation is the one reader who cannot get it. The
 * verdict buttons are simply not rendered for them, the way the rest of this
 * module hides rather than greys its writes.
 */

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting',
  APPLIED: 'Applied',
  REFUSED: 'Refused',
};

/** A rate in basis points, arriving as a string, printed as a percentage. */
const rate = (bp: string | null): string => (bp === null ? '—' : formatRate(Number(bp)));

type Verdict = { row: SplitChangeRow; kind: 'apply' | 'refuse' };

export function SplitChangeQueue({
  brandId,
  canWrite,
  revision,
  onChanged,
}: {
  /** The header's brand selector. The queue is brand-scoped; the route is too. */
  brandId: string | null;
  /** `partners.manage`. Reads are `partners.view` and are gated by the caller. */
  canWrite: boolean;
  /** Bumped by any write elsewhere on the screen, to re-read what it moved. */
  revision: number;
  onChanged: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [payload, setPayload] = useState<SplitChangePayload | null>(null);
  const [loading, setLoading] = useState(true);
  /*
   * Whether the read FAILED, as opposed to succeeding with nothing to return.
   * "No requests are waiting" and "we could not ask" are different facts about
   * a queue, and only one of them is safe to act on: an operator who reads the
   * first over a failed fetch stops looking.
   */
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<'PENDING' | 'APPLIED' | 'REFUSED' | 'ALL'>('PENDING');
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  /** Bumped by this panel's OWN writes, so a verdict re-reads the queue. */
  const [localRevision, setLocalRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ status: filter });
    if (brandId) params.set('brandId', brandId);

    api
      .get<SplitChangePayload>(`/api/admin/agents/split-changes?${params.toString()}`)
      .then((d) => {
        if (cancelled) return;
        setPayload(d);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Emptied deliberately: the previous filter's rows sitting under a
        // failure notice would be read as this filter's answer.
        setPayload(null);
        setFailed(true);
        showError({
          title: 'Could not load the split change queue',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [brandId, filter, showError, revision, localRevision]);

  const refresh = useCallback(() => {
    setLocalRevision((n) => n + 1);
    onChanged();
  }, [onChanged]);

  const close = () => {
    setVerdict(null);
    setReason('');
  };

  const submit = async () => {
    if (!verdict) return;
    setBusy(true);
    try {
      if (verdict.kind === 'apply') {
        const done = await api.post<SplitChangeApplied>(
          `/api/admin/agents/split-changes/${verdict.row.id}/apply`,
          reason.trim() ? { note: reason.trim() } : {},
        );
        toast(done.message);
      } else {
        const done = await api.post<SplitChangeRefused>(
          `/api/admin/agents/split-changes/${verdict.row.id}/refuse`,
          { reason: reason.trim() },
        );
        toast(done.message);
      }
      close();
      refresh();
    } catch (error: unknown) {
      /*
       * A POPUP MODAL, never an inline line under the button.
       *
       * Every refusal this route can produce is one an operator has to act on
       * rather than glance at: the cap has moved, somebody else decided it
       * first, the request is not theirs to see. The message names the rates
       * and the person, and it is shown where it cannot be scrolled past.
       */
      showError({
        title:
          verdict.kind === 'apply'
            ? `${verdict.row.agent_ref} was not moved`
            : `${verdict.row.agent_ref}’s request was not refused`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
      // Left OPEN on failure. Closing the dialog over a refusal would leave the
      // operator looking at an unchanged queue with no idea whether their press
      // had landed.
    } finally {
      setBusy(false);
    }
  };

  const rows = payload?.items ?? [];
  const pending = payload?.pending ?? 0;
  /*
   * Waiting requests the master's own rate has since fallen beneath.
   *
   * Counted rather than only tagged per row, because it is the one state on
   * this panel an operator has to act on rather than notice: every one of these
   * will be refused by the server if Apply is pressed, and the fix — raise the
   * master, or refuse with that as the reason — is a decision somebody has to
   * take rather than a row to scroll past. `within_cap_now` is the SERVER's
   * comparison, not one re-derived here.
   */
  const overCap = rows.filter((r) => r.status === 'PENDING' && r.within_cap_now === false);

  const columns = useMemo<Column<SplitChangeRow>[]>(
    () => [
      {
        key: 'agent',
        header: 'Sub-agent',
        render: (r) => (
          <>
            <strong>{r.agent_name}</strong>
            <div className="text-subtle" style={subLine}>
              {r.agent_ref} · {r.brand}
              {r.parent_ref ? ` · under ${r.parent_ref}` : ' · no master'}
            </div>
          </>
        ),
      },
      {
        key: 'move',
        header: 'Requested move',
        render: (r) => (
          <>
            <span className="tabular">
              {rate(r.from_rate_bp)} → {rate(r.to_rate_bp)}
            </span>
            <div className="text-subtle" style={subLine}>
              {/* The LIVE rate, printed only when it disagrees with the one the
                  request recorded. Something else moved this agent in between,
                  and applying would take the rate from a number nobody in this
                  conversation asked about. */}
              {r.from_rate_bp !== r.agent_rate_now_bp
                ? `on ${rate(r.agent_rate_now_bp)} today — it moved since the request`
                : 'unchanged since the request'}
            </div>
          </>
        ),
      },
      {
        key: 'cap',
        header: 'Master’s cap',
        render: (r) => (
          <>
            <span className="tabular">{rate(r.parent_rate_now_bp)}</span>
            <div className="text-subtle" style={subLine}>
              {r.parent_rate_bp !== r.parent_rate_now_bp
                ? `was ${rate(r.parent_rate_bp)} when asked`
                : 'unchanged since the request'}
            </div>
          </>
        ),
      },
      {
        key: 'verdict-state',
        header: 'State',
        render: (r) =>
          r.status !== 'PENDING' ? (
            <>
              <Tag tone={r.status === 'APPLIED' ? 'accent' : 'quiet'}>
                {STATUS_LABEL[r.status] ?? r.status}
              </Tag>
              <div className="text-subtle" style={subLine}>
                {r.decided_by_name ?? 'an operator'}
                {r.decided_at ? ` · ${stamp(r.decided_at)}` : ''}
              </div>
            </>
          ) : r.within_cap_now === false ? (
            <>
              <Tag tone="neutral">Over the cap</Tag>
              <div className="text-subtle" style={subLine}>
                {rate(r.to_rate_bp)} is above {r.parent_ref ?? 'the master'}’s{' '}
                {rate(r.parent_rate_now_bp)} — applying is refused
              </div>
            </>
          ) : (
            <>
              <Tag tone="outline">{STATUS_LABEL['PENDING']}</Tag>
              <div className="text-subtle" style={subLine}>
                within the cap as it stands today
              </div>
            </>
          ),
      },
      {
        key: 'asked',
        header: 'Asked',
        secondary: true,
        render: (r) => (
          <>
            <span>{stamp(r.created_at)}</span>
            <div className="text-subtle" style={subLine}>
              {r.requested_by_name ?? 'a portal login that no longer exists'}
              {r.requested_by_role ? ` · ${r.requested_by_role.toLowerCase()}` : ''}
            </div>
          </>
        ),
      },
      {
        key: 'reason',
        header: 'Why they asked',
        secondary: true,
        render: (r) => (
          <>
            <span>{r.reason}</span>
            {r.decision_reason && (
              <div className="text-subtle" style={subLine}>
                Verdict: {r.decision_reason}
              </div>
            )}
          </>
        ),
      },
      {
        key: 'decide',
        header: 'Decision',
        render: (r) => {
          if (r.status !== 'PENDING') {
            return <span className="text-subtle">decided</span>;
          }
          /*
           * NOT RENDERED for an operator without the capability, and NOT
           * rendered disabled with a `title` on it. A browser suppresses the
           * tooltip of a disabled control, so the explanation would reach
           * nobody who needed it; the sentence in the panel above carries it
           * instead, as visible text.
           */
          if (!canWrite) {
            return <span className="text-subtle">needs partners.manage</span>;
          }
          return (
            <div className="row">
              <Button
                variant="primary"
                small
                onClick={() => {
                  setReason('');
                  setVerdict({ row: r, kind: 'apply' });
                }}
              >
                Apply
              </Button>
              <Button
                small
                onClick={() => {
                  setReason('');
                  setVerdict({ row: r, kind: 'refuse' });
                }}
              >
                Refuse
              </Button>
            </div>
          );
        },
      },
    ],
    [canWrite],
  );

  return (
    <Panel title="Split change requests">
      <div className="row">
        <div className="text-subtle" style={{ fontSize: '12.5px' }}>
          {loading && !payload
            ? 'Loading…'
            : failed
              ? 'the queue could not be read'
              : pending === 0
                ? 'nothing waiting on an operator'
                : `${formatCount(pending)} waiting on an operator`}
        </div>
        <div className="row header-spacer">
          <Field label="Show">
            {({ id }) => (
              <Select
                id={id}
                value={filter}
                onChange={(e) => setFilter(e.target.value as typeof filter)}
              >
                <option value="PENDING">Waiting</option>
                <option value="APPLIED">Applied</option>
                <option value="REFUSED">Refused</option>
                <option value="ALL">All</option>
              </Select>
            )}
          </Field>
        </div>
      </div>

      {/* ── What a verdict actually does ──────────────────────────────────
          Written out because "applies from the next payout" was the sentence
          this platform used to print, and it was not true: the settlement
          engine recomputes the OPEN period in full from the agent's current
          rate on its next nightly run. An operator is entitled to know that
          the days already elapsed are repriced too.

          THE TWO MECHANISMS, NAMED HERE AND NOT ON THE SCREEN. The settled
          half is held by the trigger `trg_agent_period_result_frozen` on
          `agent.agent_period_result` (packages/db/sql/200_commission_engine.sql),
          which rejects any UPDATE of a result already CALCULATED or PAID — so
          "a settled period is never restated" is the database's promise and not
          this screen's, and the notice says that the database refuses it rather
          than printing the trigger's name, because the name is not a thing an
          operator can look up, ask anybody for, or repeat. The open half is the
          `commission.close` worker step, which on each nightly run recomputes
          the whole open period from the agent's rate as it stands at that
          moment. No screen in this console lists worker step names, so "the
          nightly commission run" is the only form of that fact an operator can
          act on.

          AND IT USED TO FINISH BY PROMISING SOMETHING NO SCREEN DOES. "The
          apply confirmation names the period and the date the repricing starts
          from" — it does not, and it never did from here. The period reference
          and the date the engine measures from come back in the APPLY
          RESPONSE, after the press, and this component reads nothing out of it
          but the one-line message; the request rows this queue is built from
          carry no open period at all, so there is nothing to name BEFORE the
          press either. The sentence was a reassurance that pointed at a dialog
          and the dialog did not keep it. What the confirmation does do now is
          repeat the repricing itself, which is the part an operator decides
          on, so that is what this says. To make the old promise true the queue
          would have to be told which period each agent settles in; worth
          doing, and worth saying here again when it is. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          <strong>Applying moves the rate now, and the open period with it.</strong> A settled
          period is never restated: the platform&rsquo;s own records refuse any change to a result that has
          already been calculated or paid, so a partner paid ₱1.2M still sees ₱1.2M and nothing
          pressed on this screen can alter that. The <strong>open</strong> period is different — the
          nightly commission run recomputes it in full from the agent&rsquo;s current rate, so the{' '}
          <strong>whole open window</strong> is repriced at the new rate, the days already elapsed
          included and not prorated from today. The master&rsquo;s override on that sub-agent moves
          across the same window. The apply confirmation repeats this before you press.
        </div>
      </div>

      {overCap.length > 0 && (
        <div className="notice">
          <Icon name="warning-circle" />
          <div>
            <strong>
              {formatCount(overCap.length)} waiting request
              {overCap.length === 1 ? '' : 's'} now sit
              {overCap.length === 1 ? 's' : ''} above the master&rsquo;s own rate.
            </strong>{' '}
            {overCap.map((r) => r.agent_ref).join(', ')} — the recruiting agent&rsquo;s rate has
            been cut since the request was raised, so the cap the request was written against is no
            longer the cap that applies. The server re-checks it at the moment Apply is pressed and
            refuses these; nothing here relies on this notice to stop them. Raise the master first,
            or refuse the request with that as the reason.
          </div>
        </div>
      )}

      {/* ── The capability, as VISIBLE TEXT ───────────────────────────────
          Not a title attribute on a disabled button: a browser suppresses the
          tooltip of a disabled control, and the one reader who needs the
          explanation is the one who cannot reach it. */}
      {!canWrite && (
        <div className="notice notice-quiet">
          <Icon name="lock-key" />
          <div>
            <strong>You can read this queue and not decide it.</strong> Applying or refusing a split
            change needs <strong>partners.manage</strong>; your role holds{' '}
            <strong>partners.view</strong>. The Apply and Refuse buttons are not shown to you rather
            than shown greyed, because a greyed button with no readable reason is a control an
            operator keeps pressing. The requests below are still yours to read, and an operator
            holding the capability can act on them.
          </div>
        </div>
      )}

      {loading && !payload ? (
        <div className="skeleton" style={{ height: '160px' }}>
          &nbsp;
        </div>
      ) : (
        <DataTable
          caption="Split change requests with the sub-agent, the requested move, the master's cap today, the state, who asked and the decision"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          empty={
            /* ── THE TWO EMPTIES ARE DIFFERENT FACTS ─────────────────────
               "Nothing is waiting" and "we could not ask" are not two wordings
               of one message, and only the first is safe to act on: an operator
               who reads an empty queue over a failed read stops looking, and a
               request a recruiting agent is waiting on sits there. So `failed`
               picks the icon, the title AND the body, and the body says which
               of the two happened instead of leaving it to be inferred.

               The real-zero sentence used to finish by naming the table the
               queue is read from, `agent.agent_split_change`, which gave an
               operator nothing they could use. What they can use is the fact
               underneath that name: the panel asks the server every time it is
               opened or filtered, so an empty answer is this moment's answer
               rather than something left over in a cache. */
            <EmptyState
              icon={failed ? 'warning-circle' : 'percent'}
              title={
                failed
                  ? 'The split change queue could not be read'
                  : filter === 'PENDING'
                    ? 'No split change is waiting'
                    : 'Nothing matches this filter'
              }
            >
              {failed
                ? 'The split-change requests did not come back, so this panel is empty because nothing was read — not because no agent has asked for a split change. The error dialog carries what it said; try again shortly.'
                : filter === 'PENDING'
                  ? 'No recruiting agent in this brand scope has a request outstanding. The queue was asked for and came back with nothing in it — a genuine zero, not a failed read. It is read fresh every time you open this panel and never from a cache, so this is how the queue stands right now.'
                  : 'No request in this brand scope is in that state.'}
            </EmptyState>
          }
        />
      )}

      {/* ── WHY `payload.settlement` IS NOT DRAWN HERE ──────────────────
          The route sends a `settlement` sentence with this payload, and this
          caption used to append it. It reads, verbatim: "Applying a split change
          moves agent.agent.commission_rate_bp. Settled commission periods are
          frozen … the OPEN period … IS recomputed in full at the new rate on
          commission.close's next nightly run." That is a column name and a
          worker step name arriving from the API at run time, printed to an
          operator — the one leak no scan over this source can see, because the
          words are not in this file.

          Nothing is lost by not drawing it. Every fact it states is already on
          this screen in words an operator has: the notice at the top of the
          panel says a settled period is never restated and the whole open
          window is repriced rather than prorated, and this caption says the cap
          is re-checked at the moment Apply is pressed. If the route is ever
          reworded to drop the two identifiers, draw it again — an API that
          answers every caller the same way about what a rate change moves is
          worth having. */}
      {rows.length > 0 && (
        <div className="text-subtle" style={captionLine}>
          A request records the rates as they were when it was raised; the columns above print those
          beside the rates that are true today. The{' '}
          <strong>cap is re-checked at the moment you press Apply</strong>, against the
          master&rsquo;s rate then — not against the snapshot on the request — so a request raised
          under a 5.0% master that has since been cut to 3.0% is refused by the server, whatever
          this table said a minute ago.
        </div>
      )}

      {/* ── WHY THIS ONE CANNOT BE DISMISSED BY ACCIDENT ──────────────────
          It read `dismissible={!busy}`, which looks like caution and is the
          opposite of it. That blocked Escape and the backdrop only WHILE THE
          WRITE WAS IN FLIGHT — the one moment the decision has already been
          taken and there is nothing left to abandon. Before the press, which
          is the moment that matters, it was freely dismissible: an operator
          who had read four rates, weighed a master's cap and typed the reason
          the next reviewer will read lost all of it to a stray Escape, with no
          word about what had happened. The guard was on the wrong side of the
          button, so it is `false` on both sides of it now. Cancel is the way
          out, and Cancel is a button somebody has to mean to press. */}
      <Dialog
        open={verdict !== null}
        onClose={close}
        dismissible={false}
        title={
          verdict?.kind === 'apply'
            ? `Apply ${verdict.row.agent_ref}’s split change`
            : `Refuse ${verdict?.row.agent_ref ?? ''}’s split change`
        }
        actions={
          <>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant={verdict?.kind === 'apply' ? 'primary' : 'secondary'}
              onClick={submit}
              disabled={busy || (verdict?.kind === 'refuse' && reason.trim().length < 8)}
            >
              {busy
                ? 'Working…'
                : verdict?.kind === 'apply'
                  ? 'Apply the new rate'
                  : 'Refuse the request'}
            </Button>
          </>
        }
      >
        {verdict && (
          <>
            <p>
              {verdict.row.agent_name} (<span className="tabular">{verdict.row.agent_ref}</span>){' '}
              {verdict.kind === 'apply' ? (
                <>
                  moves from <span className="tabular">{rate(verdict.row.agent_rate_now_bp)}</span>{' '}
                  to <span className="tabular">{rate(verdict.row.to_rate_bp)}</span>, under{' '}
                  {verdict.row.parent_ref ?? 'its master'}&rsquo;s{' '}
                  <span className="tabular">{rate(verdict.row.parent_rate_now_bp)}</span>.
                </>
              ) : (
                <>
                  stays on <span className="tabular">{rate(verdict.row.agent_rate_now_bp)}</span>.
                  Nothing is recomputed and no period moves.
                </>
              )}
            </p>

            {/* ── THE CONSEQUENCE, IN THE ONE TONE THAT FITS IT ─────────────
                Three states, and they are not equally grave, so they do not
                wear the same colour. The stylesheet's contract is that warn is
                a reversible consequence or a limit about to be met, and danger
                is the irreversible or anything that moves money.

                APPLY, WITHIN THE CAP, is danger and is the only one of the
                three that is: it moves a commission rate, and the nightly run
                then reprices the whole open period at it on BOTH sides of the
                relationship — the sub-agent's own commission and the
                recruiting agent's override on them. Money that has not been
                paid yet changes, retroactively, for two partners.

                APPLY, OVER THE CAP, is warn. It is the limit about to be met,
                word for word: the press will be refused by the server, nothing
                moves, and the operator's next step is to raise the master or
                refuse the request. This prose is the paragraph that was already
                here — kept verbatim, moved into the tone it always meant, which
                was not the accent green it was rendering in.

                REFUSE is warn. Nothing is recomputed, no money moves and the
                recruiting agent can send the request again, which is the whole
                definition of reversible. Making it danger because the word
                "refuse" is in the title would be the mistake this sweep exists
                to correct — if every confirmation is red, none of them is.

                THE SILENCE IS THE NEWS, in both of the notices that mean a
                decision lands. Nothing in this platform tells the recruiting
                agent what was decided: no mail is sent, and their portal does
                not list a split change once it has been sent. An operator who
                assumes the press answers the agent will leave somebody waiting
                on a change that was settled weeks ago, so the notice says so
                before the press rather than leaving it to be discovered when
                they ring. */}
            {verdict.kind === 'apply' ? (
              verdict.row.within_cap_now === false ? (
                <div className="notice notice-warn">
                  <Icon name="warning-circle" />
                  <div>
                    <strong>This will be refused.</strong>{' '}
                    <span className="tabular">{rate(verdict.row.to_rate_bp)}</span> is above{' '}
                    {verdict.row.parent_ref ?? 'the master'}&rsquo;s own{' '}
                    <span className="tabular">{rate(verdict.row.parent_rate_now_bp)}</span>. A
                    sub-agent&rsquo;s share cannot exceed its master&rsquo;s: the override
                    differential would go negative, and the settlement engine responds by paying the
                    master <em>nothing at all</em> on this sub-agent for the period. Raise the
                    master first, or refuse this request with that as the reason.
                  </div>
                </div>
              ) : (
                <div className="notice notice-danger">
                  <Icon name="warning" />
                  <div>
                    <strong>
                      This moves money on both sides, and the nightly run backdates it.
                    </strong>{' '}
                    {verdict.row.agent_ref} is on{' '}
                    <span className="tabular">{rate(verdict.row.to_rate_bp)}</span> from the moment
                    you press, and the nightly commission run reprices the{' '}
                    <strong>whole open period</strong> at it — the days already elapsed included,
                    not prorated from today — so {verdict.row.parent_ref ?? 'the master'}&rsquo;s
                    override on this sub-agent moves across the same window. A period already
                    calculated or paid is never restated. Nobody is told it was decided: the
                    recruiting agent&rsquo;s portal will show the new split and never your note, and
                    a request is applied once — nothing on this screen puts the rate back and the
                    agent would have to ask again.
                  </div>
                </div>
              )
            ) : (
              <div className="notice notice-warn">
                <Icon name="warning" />
                <div>
                  <strong>Nobody tells them it was refused.</strong> Your reason goes on the request
                  and into the audit trail, where the next operator to review this reads it — it
                  does not reach {verdict.row.parent_ref ?? 'the recruiting agent'}, whose portal
                  does not list a split change once it has been sent, so a refusal looks to them
                  exactly like a request still waiting. Unless you tell them yourself, they are
                  waiting on a change that is not coming. This request is decided once, and they can
                  send a new one.
                </div>
              </div>
            )}

            <div className="notice notice-quiet">
              <Icon name="user" />
              <div>
                <strong>{verdict.row.requested_by_name ?? 'A portal login'}</strong> asked on{' '}
                {stamp(verdict.row.created_at)}: &ldquo;{verdict.row.reason}&rdquo;
              </div>
            </div>

            {verdict.kind === 'apply' && verdict.row.rate_moved && (
              <div className="notice notice-quiet">
                <Icon name="clock-counter-clockwise" />
                <div>
                  A rate has moved since this was raised. The request was written against{' '}
                  <span className="tabular">{rate(verdict.row.from_rate_bp)}</span> under a{' '}
                  <span className="tabular">{rate(verdict.row.parent_rate_bp)}</span> master; today
                  they are <span className="tabular">{rate(verdict.row.agent_rate_now_bp)}</span>{' '}
                  and <span className="tabular">{rate(verdict.row.parent_rate_now_bp)}</span>. The
                  server decides against today&rsquo;s figures.
                </div>
              </div>
            )}

            {/* ── "THE AGENT IS TOLD WHY" WAS NOT TRUE ──────────────────────
                That is what this hint said, and nothing in this platform does
                it. The portal inserts a split change request and stops; no
                route reads the row back to the agent, and no mail is sent on a
                verdict. So the one sentence an operator wrote in the belief
                that it would be read by the person it answers was read by the
                next operator and by nobody else — and it told the operator
                their work here closed the conversation, which is the reason a
                refused request can sit unexplained for a month.

                The claim is gone rather than softened, and the half that was
                always true is kept in its own words. If a verdict is ever
                actually delivered to the portal or by mail, say so here again
                — that notice is worth having, and this hint is where an
                operator looks for it. */}
            <Field
              label={verdict.kind === 'apply' ? 'Note (optional)' : 'Reason'}
              hint={
                verdict.kind === 'apply'
                  ? 'Goes on the request and into the audit trail beside the rate change.'
                  : 'At least 8 characters. It goes on the request and into the audit trail, and the next operator to review this reads the same sentence. It is not sent to the agent.'
              }
            >
              {({ id, describedBy }) => (
                <textarea
                  id={id}
                  className="input"
                  rows={3}
                  aria-describedby={describedBy}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    verdict.kind === 'apply'
                      ? 'Agreed with the regional lead — Cebu has carried the branch two quarters running.'
                      : 'Branch margin is already under target this quarter; revisit at the next review.'
                  }
                />
              )}
            </Field>

            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                {verdict.kind === 'apply' ? (
                  <>
                    {/* ── THREE RECORDS, DESCRIBED AND NOT NAMED ─────────────
                        Applying moves money, so the operator pressing it is
                        owed the whole list of what moves with it. In the schema
                        that list is `agent.agent.commission_rate_bp` for the
                        sub-agent, the verdict columns on the
                        `agent.agent_split_change` row together with the parent
                        rate the apply was re-checked against, and one
                        `platform.audit_log` entry — all three written inside
                        one `withTransaction` by the route, which is what makes
                        "all three land or none do" a promise rather than a
                        hope. Not one of those three names is something an
                        operator can act on. What they CAN act on is knowing
                        there is no half-finished state to go hunting for if the
                        press fails, and that pressing twice cannot pay twice. */}
                    <strong>What this writes.</strong> Three things change together:{' '}
                    {verdict.row.agent_ref}&rsquo;s own commission rate moves to the new figure,
                    this request is stamped with your verdict and the master&rsquo;s cap it was
                    measured against, and one line is added to the audit trail. All three land or
                    none of them do, so a failure here leaves nothing half-changed. The request is
                    applied <strong>once</strong>: a second operator pressing this at the same
                    moment is refused by name.
                  </>
                ) : (
                  <>
                    <strong>What this writes.</strong> The verdict and your reason on the request
                    row, and one audit entry, in a single transaction. The sub-agent&rsquo;s rate is{' '}
                    <strong>not touched</strong>, no period is recomputed, and the agent may ask
                    again.
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Dialog>
    </Panel>
  );
}
